import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  FileText, AlertTriangle, CheckCircle, XCircle, Clock,
  Eye, Edit, Trash2, Search, Plus, TrendingUp,
  Calendar, MapPin, Building2, User, Navigation,
  Package, Scale, Briefcase, Image, Upload, Lock
} from 'lucide-react';

function ViewCompliance() {
  const [compliances, setCompliances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCompliance, setSelectedCompliance] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const itemsPerPage = 10;

  // Create axios instance
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
    timeout: 30000,
  });

  // Add token interceptor
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  const HandleAddCompliance = () => {
    window.location.href = '/admin/addcompliance';
  };

  // Fetch compliance records
  const fetchComplianceRecords = async () => {
    setLoading(true);
    try {
      const response = await api.get('/viewcompliance');
      if (response.data && response.data.data) {
        setCompliances(response.data.data);
      } else if (response.data && Array.isArray(response.data)) {
        setCompliances(response.data);
      } else {
        setCompliances([]);
      }
    } catch (error) {
      Swal.fire(
        'Error',
        error.response?.data?.message || error.message || 'Failed to load compliance records.',
        'error'
      );
      setCompliances([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComplianceRecords();
  }, []);

  // Delete compliance record
  const handleDelete = async (id, title) => {
    if (deleteConfirmId === id) {
      try {
        await api.delete(`/viewcompliance/${id}`);
        Swal.fire({
          title: 'Deleted!',
          text: 'Compliance record has been deleted successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        });
        await fetchComplianceRecords();
        setDeleteConfirmId(null);
      } catch (error) {
        Swal.fire(
          'Error',
          error.response?.data?.message || 'Failed to delete compliance record.',
          'error'
        );
      }
    } else {
      setDeleteConfirmId(id);
      setTimeout(() => setDeleteConfirmId(null), 3000);
    }
  };

  // Update compliance record (edit)
  const handleUpdate = async (compliance) => {
    const { value: formValues } = await Swal.fire({
      title: 'Edit Compliance Record',
      html: `
        <div class="text-left">
          <label class="block text-sm font-medium text-gray-700 mb-1">Compliance Type</label>
          <select id="swal-compliance_type" class="swal2-select w-full mb-3">
            <option value="expired_product" ${compliance.compliance_type === 'expired_product' ? 'selected' : ''}>⚠️ Expired Product</option>
            <option value="price_violation" ${compliance.compliance_type === 'price_violation' ? 'selected' : ''}>💰 Price Violation</option>
            <option value="no_license" ${compliance.compliance_type === 'no_license' ? 'selected' : ''}>📄 Trading Without License</option>
            <option value="short_weight" ${compliance.compliance_type === 'short_weight' ? 'selected' : ''}>⚖️ Short Weight</option>
            <option value="counterfeit" ${compliance.compliance_type === 'counterfeit' ? 'selected' : ''}>🎭 Counterfeit Product</option>
            <option value="unsafe_product" ${compliance.compliance_type === 'unsafe_product' ? 'selected' : ''}>⚠️ Unsafe Product</option>
            <option value="false_advertising" ${compliance.compliance_type === 'false_advertising' ? 'selected' : ''}>📢 False Advertising</option>
            <option value="tax_evasion" ${compliance.compliance_type === 'tax_evasion' ? 'selected' : ''}>🏦 Tax Evasion</option>
          </select>
          
          <label class="block text-sm font-medium text-gray-700 mb-1 mt-3">Trader Name</label>
          <input id="swal-trader_name" class="swal2-input w-full mb-3" value="${compliance.trader_name || ''}">
          
          <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea id="swal-description" class="swal2-textarea w-full mb-3" rows="4">${compliance.description || ''}</textarea>
          
          <label class="block text-sm font-medium text-gray-700 mb-1">Location</label>
          <input id="swal-location" class="swal2-input w-full mb-3" value="${compliance.location || ''}">
          
          <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select id="swal-status" class="swal2-select w-full">
            <option value="pending" ${compliance.status === 'pending' ? 'selected' : ''}>Pending Review</option>
            <option value="approved" ${compliance.status === 'approved' ? 'selected' : ''}>Approved</option>
            <option value="rejected" ${compliance.status === 'rejected' ? 'selected' : ''}>Rejected</option>
            <option value="under_review" ${compliance.status === 'under_review' ? 'selected' : ''}>Under Review</option>
          </select>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Update',
      cancelButtonText: 'Cancel',
      width: '600px',
      customClass: {
        popup: 'rounded-xl',
        confirmButton: 'bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg text-white',
        cancelButton: 'bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded-lg'
      },
      preConfirm: () => {
        const compliance_type = document.getElementById('swal-compliance_type').value;
        const trader_name = document.getElementById('swal-trader_name').value;
        const description = document.getElementById('swal-description').value;
        const location = document.getElementById('swal-location').value;
        const status = document.getElementById('swal-status').value;
        
        if (!trader_name) {
          Swal.showValidationMessage('Trader name is required');
          return false;
        }
        return { compliance_type, trader_name, description, location, status };
      }
    });

    if (formValues) {
      try {
        await api.put(`/viewcompliance/${compliance.id}`, formValues);
        Swal.fire({
          title: 'Updated!',
          text: 'Compliance record has been updated successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        });
        await fetchComplianceRecords();
      } catch (error) {
        Swal.fire(
          'Error',
          error.response?.data?.message || 'Failed to update compliance record.',
          'error'
        );
      }
    }
  };

  // View compliance details
  const openViewModal = (compliance) => {
    setSelectedCompliance(compliance);
    setIsViewModalOpen(true);
  };

  // Get violation badge color
  const getViolationColor = (type) => {
    const colors = {
      expired_product: 'bg-red-100 text-red-800',
      price_violation: 'bg-yellow-100 text-yellow-800',
      no_license: 'bg-orange-100 text-orange-800',
      short_weight: 'bg-purple-100 text-purple-800',
      counterfeit: 'bg-pink-100 text-pink-800',
      unsafe_product: 'bg-red-100 text-red-800',
      false_advertising: 'bg-blue-100 text-blue-800',
      tax_evasion: 'bg-indigo-100 text-indigo-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getViolationLabel = (type) => {
    const labels = {
      expired_product: 'Expired Product',
      price_violation: 'Price Violation',
      no_license: 'No License',
      short_weight: 'Short Weight',
      counterfeit: 'Counterfeit',
      unsafe_product: 'Unsafe Product',
      false_advertising: 'False Advertising',
      tax_evasion: 'Tax Evasion'
    };
    return labels[type] || type;
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch(status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'under_review':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Calculate statistics
  const stats = {
    total: compliances.length,
    expired_product: compliances.filter(c => c.compliance_type === 'expired_product').length,
    price_violation: compliances.filter(c => c.compliance_type === 'price_violation').length,
    no_license: compliances.filter(c => c.compliance_type === 'no_license').length,
    short_weight: compliances.filter(c => c.compliance_type === 'short_weight').length,
    approved: compliances.filter(c => c.status === 'approved').length,
    pending: compliances.filter(c => c.status === 'pending').length,
    rejected: compliances.filter(c => c.status === 'rejected').length,
    under_review: compliances.filter(c => c.status === 'under_review').length,
  };

  // Filter compliance records
  const filteredCompliances = compliances.filter(compliance => {
    const matchesSearch = 
      compliance.trader_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      compliance.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      compliance.location?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' ? true : compliance.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCompliances.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCompliances.length / itemsPerPage);




    
   if (loading) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
      <div className="text-center">
        <div className="relative inline-block">
          {/* Spinner */}
          <div className="w-16 h-16 border-4 border-indigo-200 rounded-full"></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-indigo-700 rounded-full animate-spin border-t-transparent"></div>
          
          {/* Inner icon */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <svg className="w-6 h-6 text-indigo-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
        </div>
        
        <h3 className="mt-6 text-xl font-semibold text-gray-800">Loading Your Data</h3>
        <p className="mt-2 text-gray-500">Please wait while we fetch your data...</p>
        
        {/* Loading steps */}
        <div className="mt-6 space-y-2">
          {['Fetching files', 'Loading statistics', 'Preparing all'].map((step, index) => (
            <div key={step} className="flex items-center justify-center space-x-2 text-sm text-gray-500">
              <div className={`w-1.5 h-1.5 rounded-full ${index === 0 ? 'bg-indigo-600 animate-pulse' : 'bg-gray-300'}`}></div>
              <span className={index === 0 ? 'text-indigo-600' : ''}>{step}</span>
              {index === 0 && (
                <svg className="w-3 h-3 text-indigo-600 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <div className="flex items-center gap-3">
                <FileText className="h-10 w-10 text-indigo-600" />
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    Compliance Records
                  </h1>
                  <p className="text-gray-600 mt-2">View and manage all compliance reports</p>
                </div>
              </div>
            </div>
            <button
              onClick={HandleAddCompliance}
              className="bg-indigo-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-indigo-700 transform hover:scale-105 transition-all duration-200 flex items-center gap-2 shadow-lg"
            >
              <Plus className="h-5 w-5" />
              Add Compliance
            </button>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-indigo-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-xs font-medium">Total Records</p>
                  <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
                </div>
                <div className="bg-indigo-100 rounded-full p-2">
                  <FileText className="h-5 w-5 text-indigo-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-xs font-medium">Approved</p>
                  <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
                </div>
                <div className="bg-green-100 rounded-full p-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-yellow-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-xs font-medium">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                </div>
                <div className="bg-yellow-100 rounded-full p-2">
                  <Clock className="h-5 w-5 text-yellow-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-red-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-xs font-medium">Rejected</p>
                  <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
                </div>
                <div className="bg-red-100 rounded-full p-2">
                  <XCircle className="h-5 w-5 text-red-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Violation Type Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <div className="bg-red-50 rounded-lg p-3 text-center">
              <p className="text-xs text-red-600 font-medium">Expired Products</p>
              <p className="text-xl font-bold text-red-700">{stats.expired_product}</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3 text-center">
              <p className="text-xs text-yellow-600 font-medium">Price Violations</p>
              <p className="text-xl font-bold text-yellow-700">{stats.price_violation}</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-3 text-center">
              <p className="text-xs text-orange-600 font-medium">No License</p>
              <p className="text-xl font-bold text-orange-700">{stats.no_license}</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 text-center">
              <p className="text-xs text-purple-600 font-medium">Short Weight</p>
              <p className="text-xl font-bold text-purple-700">{stats.short_weight}</p>
            </div>
          </div>

          {/* Search and Filter */}
          <div className="mt-6 flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by trader name, description or location..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none bg-white"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none bg-white"
            >
              <option value="all">All Status</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
              <option value="under_review">Under Review</option>
            </select>
          </div>
        </div>

        {/* Compliance Table */}
        {currentItems.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-md p-12 text-center">
            <AlertTriangle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg font-medium">No compliance records found</p>
            <p className="text-gray-400 text-sm mt-2">
              {searchTerm ? 'Try adjusting your search' : 'Add your first compliance record to get started'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-indigo-700">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Trader Name</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Violation Type</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell">Description</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden lg:table-cell">Location</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden sm:table-cell">Date</th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-white uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentItems.map((item) => (
                    <tr key={item.id} className="hover:bg-indigo-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-gray-900">{item.trader_name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getViolationColor(item.compliance_type)}`}>
                          {getViolationLabel(item.compliance_type)}
                        </span>
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell">
                        <div className="text-sm text-gray-600 max-w-xs truncate">
                          {item.description || 'No description'}
                        </div>
                      </td>
                      <td className="px-6 py-4 hidden lg:table-cell">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <MapPin className="h-3 w-3" />
                          {item.location || 'N/A'}
                        </div>
                       </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.status)}`}>
                          {item.status ? item.status.replace('_', ' ').toUpperCase() : 'PENDING'}
                        </span>
                       </td>
                      <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                        <div className="text-sm text-gray-600">
                          {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}
                        </div>
                       </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => openViewModal(item)}
                            className="p-1 text-indigo-600 hover:text-indigo-800 transition rounded-lg hover:bg-indigo-100"
                            title="View Details"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleUpdate(item)}
                            className="p-1 text-blue-600 hover:text-blue-800 transition rounded-lg hover:bg-blue-100"
                            title="Edit"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id, item.trader_name)}
                            className={`p-1 transition rounded-lg ${
                              deleteConfirmId === item.id
                                ? 'bg-red-600 text-white px-2 py-1'
                                : 'text-red-600 hover:text-red-800 hover:bg-red-100'
                            }`}
                            title={deleteConfirmId === item.id ? 'Confirm Delete' : 'Delete'}
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                       </td>
                     </tr>
                  ))}
                </tbody>
               </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(indexOfLastItem, filteredCompliances.length)}
                  </span>{' '}
                  of <span className="font-medium">{filteredCompliances.length}</span> records
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <div className="flex gap-1">
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-1 rounded-lg transition-colors ${
                            currentPage === pageNum
                              ? 'bg-indigo-600 text-white'
                              : 'border border-gray-300 text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* View Detail Modal */}
      {isViewModalOpen && selectedCompliance && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  Compliance Details
                </h2>
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Trader/Business Name</p>
                  <p className="text-base font-medium text-gray-900">{selectedCompliance.trader_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Violation Type</p>
                  <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${getViolationColor(selectedCompliance.compliance_type)}`}>
                    {getViolationLabel(selectedCompliance.compliance_type)}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedCompliance.status)}`}>
                    {selectedCompliance.status ? selectedCompliance.status.replace('_', ' ').toUpperCase() : 'PENDING'}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date Reported</p>
                  <p className="text-base font-medium">{selectedCompliance.created_at ? new Date(selectedCompliance.created_at).toLocaleString() : 'N/A'}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-sm text-gray-500">Description</p>
                  <p className="text-base font-medium">{selectedCompliance.description || 'No description'}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="text-base font-medium flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    {selectedCompliance.location || 'No location specified'}
                  </p>
                </div>
                {selectedCompliance.latitude && selectedCompliance.longitude && (
                  <div className="sm:col-span-2">
                    <p className="text-sm text-gray-500">GPS Coordinates</p>
                    <p className="text-sm font-mono text-gray-600">
                      Lat: {selectedCompliance.latitude}, Lng: {selectedCompliance.longitude}
                    </p>
                  </div>
                )}
                {selectedCompliance.product_name && (
                  <div>
                    <p className="text-sm text-gray-500">Product Name</p>
                    <p className="text-base font-medium">{selectedCompliance.product_name}</p>
                  </div>
                )}
                {selectedCompliance.price_amount && (
                  <div>
                    <p className="text-sm text-gray-500">Price Amount</p>
                    <p className="text-base font-medium">ETB {selectedCompliance.price_amount}</p>
                  </div>
                )}
                {selectedCompliance.license_number && (
                  <div>
                    <p className="text-sm text-gray-500">License Number</p>
                    <p className="text-base font-medium">{selectedCompliance.license_number}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ViewCompliance;